import { useState } from 'react';
import styled from 'styled-components';
import { Response } from 'got';
import { requestService } from 'shared/utils/services/requestService';
import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { variableFontRegular, variableFontMedium } from 'styles/theme/text';
import FAQ from 'paymentVerifications/components/Faq';
import VerificationCodeForm from 'paymentVerifications/components/VerificationCodeForm';
import {
  PAYMENT_VERIFICATIONS_CONFIRM_MESSAGE,
  PAYMENT_VERIFICATIONS_DEFAULT_ERR_HEADER,
  PAYMENT_VERIFICATIONS_DISCLAIMER,
  PAYMENT_VERIFICATIONS_NO_CODE_OR_ORDER_ERR,
  PAYMENT_VERIFICATIONS_ORDER_NOT_FOUND_MESSAGE,
  PAYMENT_VERIFICATIONS_TOO_MANY_TRIES_MESSAGE,
  PAYMENT_VERIFICATIONS_MAX_WIDTH,
  PAYMENT_VERIFICATIONS_PHONE_GUTTER,
} from 'paymentVerifications/enums/paymentVerificationsPageEnums';

interface IPaymentVerification {
  heading?: string;
  message?: string;
}

interface IPaymentVerificationsPage extends IPaymentVerification {
  orderAccessToken?: string;
}

type messages = {
  // eslint-disable-next-line camelcase
  localized_messages?: string[];
  messages?: string[];
};

const PaymentVerificationsPage = ({
  heading,
  message,
  orderAccessToken,
}: IPaymentVerificationsPage) => {
  const [headerMessage, setHeaderMessage] = useState(heading);
  const [subtitleMessage, setSubtitleMessage] = useState(message);
  const [isLoading, setIsLoading] = useState(false);

  const isConfirmingCode = headerMessage === PAYMENT_VERIFICATIONS_CONFIRM_MESSAGE;
  const isRestrictedFromSendingCodes =
    subtitleMessage === PAYMENT_VERIFICATIONS_TOO_MANY_TRIES_MESSAGE;
  const shouldShowCodeForm = orderAccessToken && isConfirmingCode && !isRestrictedFromSendingCodes;

  const handleSubmitVerificationCode = async (code: string) => {
    if (!orderAccessToken && !code) {
      setSubtitleMessage(PAYMENT_VERIFICATIONS_NO_CODE_OR_ORDER_ERR);
      return;
    }
    try {
      setIsLoading(true);
      const paymentVerificationMessaging = await requestService({
        url: `/web-api/v1/orders/${orderAccessToken}/payment_verification`,
        method: 'POST',
        data: JSON.stringify({
          statement_code: code,
        }),
      });
      setHeaderMessage(paymentVerificationMessaging.heading);
      setSubtitleMessage(paymentVerificationMessaging.message);
    } catch (error) {
      setSubtitleMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PaymentVerificationsPage.Content>
      <PaymentVerificationsPage.Heading>{headerMessage}</PaymentVerificationsPage.Heading>
      {subtitleMessage && (
        <PaymentVerificationsPage.Message>{subtitleMessage}</PaymentVerificationsPage.Message>
      )}
      {shouldShowCodeForm && (
        <>
          <PaymentVerificationsPage.VerificationCodeForm
            onSubmit={handleSubmitVerificationCode}
            isLoading={isLoading}
          />
          <PaymentVerificationsPage.Disclaimer>
            {PAYMENT_VERIFICATIONS_DISCLAIMER}
          </PaymentVerificationsPage.Disclaimer>
        </>
      )}
      <PaymentVerificationsPage.FAQ />
    </PaymentVerificationsPage.Content>
  );
};

PaymentVerificationsPage.Content = styled.div`
  margin: 20px ${PAYMENT_VERIFICATIONS_PHONE_GUTTER} 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    width: calc(100% - 180px);
    margin: 50px auto 0;
  }
`;
PaymentVerificationsPage.Heading = styled.h1`
  line-height: 1;
  max-width: ${PAYMENT_VERIFICATIONS_MAX_WIDTH};
  margin: 0 auto 16px;
  text-align: center;
  ${variableFontMedium({ size: '32px' })}
`;
PaymentVerificationsPage.Message = styled.p`
  max-width: ${PAYMENT_VERIFICATIONS_MAX_WIDTH};
  margin: 0 auto 48px;
  text-align: center;
  ${variableFontRegular({ size: '16px' })}
`;
PaymentVerificationsPage.VerificationCodeForm = styled(VerificationCodeForm)`
  width: 100%;
  max-width: ${PAYMENT_VERIFICATIONS_MAX_WIDTH};
  margin: 0 auto 16px;
`;
PaymentVerificationsPage.Disclaimer = styled.p`
  max-width: ${PAYMENT_VERIFICATIONS_MAX_WIDTH};
  margin: 0 auto 48px;
  text-align: center;
  ${variableFontRegular({ size: '16px' })}
  line-height: 1.25;
`;
PaymentVerificationsPage.FAQ = styled(FAQ)`
  max-width: ${PAYMENT_VERIFICATIONS_MAX_WIDTH};
  margin: 0 auto 48px;
`;

export const getServerSideProps = async ({ req, res, query }) => {
  const { order: orderAccessToken }: { order: string } = query;
  if (orderAccessToken) {
    try {
      const data: IPaymentVerification = await sneakersRequest(req, res)
        .get(`api/v1/orders/${orderAccessToken}/payment_verification`)
        .then((response: Response) => {
          if (response.statusCode < 200 || response.statusCode > 399) {
            const responseBody: messages = response.body;
            throw new Error(responseBody.localized_messages[0] || responseBody.messages[0]);
          } else {
            return response.body;
          }
        });
      return {
        props: { message: data.message, heading: data.heading, orderAccessToken },
      };
    } catch (error) {
      return {
        props: {
          heading: PAYMENT_VERIFICATIONS_DEFAULT_ERR_HEADER,
          message: error.message,
          orderAccessToken,
        },
      };
    }
  } else {
    return {
      props: {
        heading: PAYMENT_VERIFICATIONS_DEFAULT_ERR_HEADER,
        message: PAYMENT_VERIFICATIONS_ORDER_NOT_FOUND_MESSAGE,
      },
    };
  }
};

export default PaymentVerificationsPage;
